// src/App.js

import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import PrivateRoute from './routes/PrivateRoute'
import './i18n'
import { useTranslation } from 'react-i18next'
import Navbar from './components/Navbar'
import AppErrorBoundary from './components/AppErrorBoundary'

import Profile from './pages/Profile'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import DashboardPage from './pages/DashboardPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AoiDefinitionPage from './pages/AoiDefinitionPage'
import FusionSurvey from './surveys/fusion-con-kaz'
import FusionDoctorSurvey from './surveys/fusion-doc-kaz'
import FusionUkraineSurvey from './surveys/fusion-con-ua'
import FusionDoctorUkraineSurvey from './surveys/fusion-doc-ua'
import SurveyReportPage from './pages/SurveyReportPage'
import AdminRecordingReportPage from './pages/AdminRecordingReportPage'
import EurobankImplicitRt from './surveys/eurobankImplicitRt'
import GenZMkSurvey from './surveys/genZMkSurvey'
import GenZRsSurvey from './surveys/genZRsSurvey'
import GenZXkSurvey from './surveys/genZXkSurvey'
import GenZMeSurvey from './surveys/genZMeSurvey'

const PUBLIC_SURVEY_PATHS = [
  '/fusion-con-ka',
  '/fusion-doc-ka',
  '/fusion-con-ua',
  '/fusion-doc-ua',
  '/damas-ad-test',
  '/gen-z-mk',
  '/gen-z-rs',
  '/gen-z-xk',
  '/gen-z-me',
]

function AppContent() {
  const { pathname } = useLocation()

  return (
    <>
      {!PUBLIC_SURVEY_PATHS.includes(pathname) && <Navbar />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route path="/" element={<Navigate to="/ProjectsPage" replace />} />

        <Route
          path="/ProjectsPage"
          element={
            <PrivateRoute roles={['user', 'admin']}>
              <DashboardPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin-users"
          element={
            <PrivateRoute adminOnly>
              <AdminUsersPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/aoi/new"
          element={
            <PrivateRoute>
              <AoiDefinitionPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/aoi/:definitionId"
          element={
            <PrivateRoute>
              <AoiDefinitionPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/fusion-con-ka"
          element={<FusionSurvey />}
        />

        <Route
          path="/fusion-doc-ka"
          element={<FusionDoctorSurvey />}
        />

        <Route
          path="/fusion-con-ua"
          element={<FusionUkraineSurvey />}
        />

        <Route
          path="/fusion-doc-ua"
          element={<FusionDoctorUkraineSurvey />}
        />

        <Route path="/damas-ad-test" element={<EurobankImplicitRt />} />
        <Route path="/gen-z-mk" element={<GenZMkSurvey />} />
        <Route path="/gen-z-rs" element={<GenZRsSurvey />} />
        <Route path="/gen-z-xk" element={<GenZXkSurvey />} />
        <Route path="/gen-z-me" element={<GenZMeSurvey />} />

        <Route
          path="/reports/survey"
          element={
            <PrivateRoute roles={['admin', 'report_viewer']}>
              <SurveyReportPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/reports/recordings"
          element={
            <PrivateRoute adminOnly>
              <AdminRecordingReportPage />
            </PrivateRoute>
          }
        />

        <Route path="*" element={<Navigate to="/ProjectsPage" replace />} />
      </Routes>
    </>
  )
}

function PublicAwareApp() {
  const { pathname } = useLocation()

  if (PUBLIC_SURVEY_PATHS.includes(pathname)) {
    return <AppContent />
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

function App() {
  const { i18n } = useTranslation()

  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return (
    <Router>
      <AppErrorBoundary>
        <PublicAwareApp />
      </AppErrorBoundary>
    </Router>
  )
}

export default App
